import { type ReactNode, type HTMLAttributes, type ElementType } from "react";
import { cn } from "../lib/cn";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type BottomNavbarVariant = "default" | "floating";

export interface BottomNavbarProps extends HTMLAttributes<HTMLElement> {
  /**
   * Style de la Bottom Navbar :
   * - `default` : Barre inférieure ancrée sur toute la largeur avec support des zones sûres iOS (`pb-safe`).
   * - `floating` : Dock îlot flottant au-dessus du bas de l'écran avec bordure arrondie et verre dépoli.
   */
  variant?: BottomNavbarVariant;
  /** Fixe la barre en bas du port d'affichage (par défaut: true). */
  fixed?: boolean;
  /** Afficher une bordure supérieure (pour variant="default"). */
  bordered?: boolean;
  children?: ReactNode;
}

/* -------------------------------------------------------------------------- */
/* BottomNavbar (Root)                                                        */
/* -------------------------------------------------------------------------- */

export function BottomNavbar({
  variant = "default",
  fixed = true,
  bordered = true,
  className,
  children,
  ...props
}: BottomNavbarProps) {
  return (
    <nav
      className={cn(
        // Cf. `Navbar` : jamais `transition-all` sur un conteneur fixé, elle
        // embarque la géométrie et fait travailler la mise en page pour rien.
        "w-full transition-[background-color,border-color] duration-normal ease-standard",
        fixed && "fixed bottom-0 left-0 right-0 z-40",
        variant === "floating" && "p-3 sm:p-4 pointer-events-none pb-safe",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "mx-auto flex items-center justify-around transform-gpu transition-[transform,opacity] duration-normal ease-standard",
          variant === "default" && [
            "w-full bg-surface/90 backdrop-blur-xl border-t border-border px-2 py-2 pb-safe",
            bordered && "border-t border-border",
          ],
          variant === "floating" && [
            "pointer-events-auto max-w-lg rounded-full border border-border/80",
            "bg-surface/85 backdrop-blur-2xl shadow-xl shadow-black/10 dark:shadow-black/30",
            "px-3 py-2",
          ],
        )}
      >
        {children}
      </div>
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/* BottomNavItem                                                              */
/* -------------------------------------------------------------------------- */

export interface BottomNavItemProps extends HTMLAttributes<HTMLElement> {
  icon: ReactNode;
  label: string;
  active?: boolean;
  badge?: ReactNode | number | string;
  href?: string;
  as?: ElementType;
  onClick?: () => void;
}

export function BottomNavItem({
  icon,
  label,
  active = false,
  badge,
  href,
  as: Component = href ? "a" : "button",
  onClick,
  className,
  ...props
}: BottomNavItemProps) {
  return (
    <Component
      href={href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative group flex flex-col items-center justify-center gap-1 min-w-[64px] py-1 px-2 rounded-xl select-none",
        "transition-[color,background-color] duration-fast ease-standard",
        "focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2",
        active
          ? "text-accent-fg font-semibold"
          : "text-fg-muted hover:text-fg hover:bg-surface-sunken/50",
        className,
      )}
      {...props}
    >
      {/* Conteneur d'icône avec pilule d'arrière-plan actif */}
      <div className="relative flex items-center justify-center">
        {active && (
          <span className="absolute inset-x-[-8px] inset-y-[-2px] -z-10 transform-gpu animate-pop rounded-full bg-accent-subtle/80" />
        )}

        <div
          className={cn(
            "transform-gpu transition-transform duration-fast ease-standard group-active:scale-90",
            active && "scale-110 text-accent",
          )}
        >
          {icon}
        </div>

        {/* Badge de notification */}
        {badge !== undefined && badge !== null && (
          <span className="absolute -top-1 -right-2.5 flex items-center justify-center">
            {typeof badge === "boolean" || badge === "" ? (
              <span className="size-2.5 rounded-full bg-accent ring-2 ring-surface animate-pulse" />
            ) : (
              <span className="min-w-[18px] h-4 px-1 flex items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-fg ring-2 ring-surface">
                {badge}
              </span>
            )}
          </span>
        )}
      </div>

      {/* Label de navigation */}
      <span
        className={cn(
          "text-[11px] leading-tight tracking-tight transition-colors duration-fast ease-standard",
          active ? "font-semibold text-accent-fg" : "font-medium text-fg-muted group-hover:text-fg",
        )}
      >
        {label}
      </span>
    </Component>
  );
}
